import * as React from "react";
import { NumberWithErrorProps } from "../../interfaces/props/NumberWithErrorProps";
import { TextField } from "@fluentui/react";
import * as lcid from "../../../../utilities/lcid/lcid";
import { localStringToNumber } from "../helpers/stringHelper";
import { logger } from "../../../../utilities/logger";
import { decimalSteps } from "../../config/constants";
import { inputResult } from "../helpers/inputResult";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";

export const Number: React.FunctionComponent<NumberWithErrorProps> = ({
  displayName,
  isRequired,
  inputValue,
  min,
  max,
  error,
  regionalSetting,
  updateInputValidationStatus,
  inputValidator,
  decimals
}: NumberWithErrorProps) => {

  const [numberFormatter] = React.useState<Intl.NumberFormat>(new Intl.NumberFormat(lcid.from(regionalSetting?.CollationLCID).replace("_", "-"), { maximumFractionDigits: decimals ?? 2 }));
  const [isInFocus, setIsInFocus] = React.useState<boolean>(false);
  const [formattedNumberValue, setFormattedNumberValue] = React.useState<string>();
  const [numericInputError, setNumericInputError] = React.useState<string>();

  const isFirstRun = React.useRef(true);

  React.useEffect(() => {
    try {
      setFormattedNumberValue(inputValue?.value?.toString());
    } catch (e) {
      logger(e, "number.tsx (useEffect(inputValue?.value))")
    }
  }, [inputValue?.value]);

  React.useEffect(() => {
    try {
      if (isFirstRun.current) {
        isFirstRun.current = false;
        return;
      }
      updateInputValidationStatus(inputValidator.validate(inputValue?.value));
    } catch (e) {
      logger(e, "number.tsx (useEffect)")
    }
  }, [inputValue?.updatedFromDataSourceToggle]);

  const handleNumberChange = (event: any, newValue?: string) => {
    // updateInputValidationStatus(inputValidator.validate(newValue));
    const validationResult: inputResult = inputValidator.validate(newValue);
    if (newValue) {
      setNumericInputError(validationResult?.error);
    }
    updateInputValidationStatus(validationResult);
  };

  const onChangeTextField = React.useCallback((event: any, value?: string) => {
    return;
  }, []);

  const onBlur = (event: any): void => {
    const validationResult: inputResult = inputValidator.validate(event.target.value);
    setNumericInputError(validationResult?.error);
    updateInputValidationStatus(validationResult);
    if (event.target.value) {
      const numberValue = localStringToNumber(event.target.value);
      setFormattedNumberValue(numberFormatter.format(numberValue));
    } else {
      setFormattedNumberValue(null);
    }
    setIsInFocus(false);
  };

  const onFocus = (event: any): void => {
    setIsInFocus(true);
  };

  const numberInputField = (<TextField
    label={displayName}
    disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
    type="number"
    step={decimals !== undefined ? decimalSteps[decimals] : decimalSteps[2]}
    min={min !== undefined ? min : ""}
    max={max !== undefined ? max : ""}
    value={inputValue?.value ?? ""}
    onChange={handleNumberChange}
    onBlur={onBlur}
    required={isRequired()}
    errorMessage={numericInputError}
    className="xb-sp-input"
  />);

  const textInputField = (
    <TextField
      label={displayName}
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      type="text"
      value={formattedNumberValue ?? ""}
      onChange={onChangeTextField}
      onFocus={onFocus}
      required={isRequired()}
      errorMessage={error}
      className="xb-sp-input"
    />
  );

  return isInFocus ? numberInputField : textInputField;
};

export default Number;
